// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import DateTimeInput from "@/UI/inputs/DateTimeInput";
import NumberInput from "@/UI/inputs/NumberInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyPaymentFormDetails = {
    planId: string;
    /** What this row still needs, offered as the default — the common case is paying exactly it. */
    suggested?: number | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * Money in, against the **plan** rather than against a row.
 *
 * That is the whole design of installments here: one payment of 25,000 settles two 10,000
 * installments and half of a third, and `allocatePlan` spreads it in due order. Recording it on a
 * row would mean splitting it by hand across three, and correcting it later would mean unpicking
 * all three. `paidAmount` on an installment is an answer, never an input.
 */
export default function PaymentFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { planId, suggested, onDone } = details as TyPaymentFormDetails;

    const [amount, setAmount] = useState<number | null>(suggested ?? null);
    const [paidAt, setPaidAt] = useState<string | null>(() => new Date().toISOString());
    const [note, setNote] = useState("");

    const { loading, error, succeededAt, invoke } = useFetch(`/sales/plans/${planId}/payments`, {}, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.sales.addPayment}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <NumberInput value={amount} onChange={setAmount} label={t.sales.paymentAmount} suffix={t.properties.currency} disabled={loading} />
                <DateTimeInput value={paidAt} onChange={setPaidAt} label={t.sales.paidAt} dateOnly max={new Date()} disabled={loading} />
                <TextInput value={note} onChange={setNote} label={t.crm.fields.notes} multiline rows={2} disabled={loading} />

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button
                    type="button"
                    className="side-form__submit"
                    onClick={() => invoke({ method: "POST", data: { amount, paidAt, note: note.trim() || null } })}
                    disabled={loading || !amount || amount <= 0}
                >
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
